<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * Revenue or registrations over time, as one line or split by category.
 *
 * Hand-drawn SVG: the project carries no charting dependency, and a handful of line series does
 * not justify adding one to a bundle the legacy admin loads on every page.
 *
 * A day with no sales in a category is ABSENT from that category's points, not zero - the server
 * only emits days that had rows. Points are therefore placed by date against a shared axis
 * rather than by index, or two series with different day counts would drift apart.
 */
const props = defineProps({
  /** Every day in the window, from the ungrouped series - the x axis. */
  days: {
    type: Array,
    default: () => [],
  },
  /** [{ key, label, points: [{ day, revenue, units }] }]. One entry = a single line. */
  series: {
    type: Array,
    default: () => [],
  },
  /** 'revenue' or 'units'. */
  metric: {
    type: String,
    default: 'revenue',
  },
  /** [{ day, net }] - drawn only alongside a single unsplit revenue line. */
  collected: {
    type: Array,
    default: () => [],
  },
});

// Measured, not fixed. The chart used to declare a 760-unit viewBox and stretch it across
// whatever width it was given with preserveAspectRatio="none", which scales the geometry AND the
// text - the axis labels came out wide and soft. Matching the viewBox to the real pixel width
// keeps one unit equal to one pixel, so type and strokes render at their true size.
const container = ref(null);
const measuredWidth = ref(760);
let observer = null;

onMounted(() => {
  if (!container.value) {
    return;
  }
  if (typeof ResizeObserver === 'undefined') {
    measuredWidth.value = container.value.clientWidth || 760;
    return;
  }
  observer = new ResizeObserver(entries => {
    const width = entries[0]?.contentRect?.width;
    if (width) {
      measuredWidth.value = Math.round(width);
    }
  });
  observer.observe(container.value);
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});

const WIDTH = computed(() => Math.max(320, measuredWidth.value));
const HEIGHT = 240;
const PAD_LEFT = 6;
const PAD_RIGHT = 52;
const PAD_TOP = 12;
const PAD_BOTTOM = 26;

const plotWidth = computed(() => WIDTH.value - PAD_LEFT - PAD_RIGHT);
const plotHeight = HEIGHT - PAD_TOP - PAD_BOTTOM;

// Ordered so the first and biggest series gets the brand colour.
const PALETTE = [
  'var(--pfs-teal)',
  'var(--pfs-red)',
  'var(--pfs-orange)',
  'var(--teal-dark)',
  'var(--green-light)',
  'var(--gray-dark)',
  'var(--red-light)',
  'var(--orange-light)',
];

const isMoney = computed(() => props.metric === 'revenue');
const isSingle = computed(() => props.series.length === 1);

/** Date -> x position, shared by every series so they line up. */
const xByDay = computed(() => {
  const map = new Map();
  const count = props.days.length;
  props.days.forEach((day, index) => {
    map.set(day, PAD_LEFT + (count <= 1 ? plotWidth.value / 2 : (index / (count - 1)) * plotWidth.value));
  });
  return map;
});

const maxValue = computed(() => {
  let peak = 0;
  props.series.forEach(entry => entry.points.forEach(point => {
    peak = Math.max(peak, Number(point[props.metric]) || 0);
  }));
  if (isSingle.value && isMoney.value) {
    props.collected.forEach(point => { peak = Math.max(peak, Number(point.net) || 0); });
  }
  return peak > 0 ? peak : 1;
});

/**
 * Y position for a value.
 * @param {number} value Value.
 * @returns {number}
 */
function toY(value) {
  return PAD_TOP + plotHeight - ((value / maxValue.value) * plotHeight);
}

/**
 * Build the path for one series, skipping days it has no row for.
 * @param {object[]} points Points.
 * @param {string} field Field to plot.
 * @returns {string}
 */
function toPath(points, field) {
  const usable = points
    .filter(point => xByDay.value.has(point.day))
    .map(point => ({ x: xByDay.value.get(point.day), y: toY(Number(point[field]) || 0) }));

  return usable.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

const lines = computed(() => props.series.map((entry, index) => ({
  key: entry.key,
  label: entry.label,
  color: PALETTE[index % PALETTE.length],
  path: toPath(entry.points, props.metric),
  // The single-line case keeps the filled area that made the original chart readable at a
  // glance; stacked fills across many series would just be mud.
  area: isSingle.value ? areaPath(entry.points, props.metric) : '',
})));

/**
 * Close a line down to the baseline for the filled variant.
 * @param {object[]} points Points.
 * @param {string} field Field.
 * @returns {string}
 */
function areaPath(points, field) {
  const usable = points.filter(point => xByDay.value.has(point.day));
  if (usable.length === 0) {
    return '';
  }
  const base = PAD_TOP + plotHeight;
  const first = xByDay.value.get(usable[0].day);
  const last = xByDay.value.get(usable[usable.length - 1].day);
  return `${toPath(points, field)} L${last.toFixed(1)},${base} L${first.toFixed(1)},${base} Z`;
}

const collectedPath = computed(() => (isSingle.value && isMoney.value
  ? toPath(props.collected.map(p => ({ day: p.day, revenue: p.net })), 'revenue')
  : ''));

/**
 * Axis label for a value.
 * @param {number} amount Value.
 * @returns {string}
 */
function formatAxis(amount) {
  if (!isMoney.value) {
    return amount >= 1000 ? `${Math.round(amount / 1000)}K` : `${Math.round(amount)}`;
  }
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${Math.round(amount / 1000)}K`;
  }
  return `$${Math.round(amount)}`;
}

const gridLines = computed(() => [0, 0.25, 0.5, 0.75, 1].map(fraction => ({
  fraction,
  y: PAD_TOP + plotHeight - (fraction * plotHeight),
  label: formatAxis(maxValue.value * fraction),
})));

const dateTicks = computed(() => {
  const days = props.days;
  if (days.length < 2) {
    return [];
  }
  return [0, 0.33, 0.66, 1]
    .map(fraction => Math.round((days.length - 1) * fraction))
    .filter((index, position, all) => all.indexOf(index) === position)
    .map(index => ({
      key: days[index],
      x: xByDay.value.get(days[index]),
      label: new Date(`${days[index]}T00:00:00`)
        .toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    }));
});
</script>

<template>
  <div ref="container" :class="$style.chartArea">
    <p v-if="days.length === 0" :class="$style.empty">
      No days in this window.
    </p>

    <template v-else>
      <svg
        :class="$style.chart"
        :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
        :width="WIDTH"
        :height="HEIGHT"
        role="img"
        :aria-label="isMoney ? 'Revenue over time' : 'Registrations over time'"
      >
        <line
          v-for="line in gridLines"
          :key="line.fraction"
          :x1="PAD_LEFT"
          :y1="line.y"
          :x2="WIDTH - PAD_RIGHT"
          :y2="line.y"
          :class="$style.grid"
        />
        <text
          v-for="line in gridLines"
          :key="`label-${line.fraction}`"
          :x="WIDTH - PAD_RIGHT + 6"
          :y="line.y + 4"
          :class="$style.axisLabel"
        >{{ line.label }}</text>

        <path
          v-for="line in lines"
          v-show="line.area"
          :key="`area-${line.key}`"
          :d="line.area"
          :fill="line.color"
          :class="$style.area"
        />
        <path
          v-for="line in lines"
          :key="`line-${line.key}`"
          :d="line.path"
          :stroke="line.color"
          :class="$style.line"
        />
        <path v-if="collectedPath" :d="collectedPath" :class="$style.collected" />

        <text
          v-for="tick in dateTicks"
          :key="tick.key"
          :x="tick.x"
          :y="HEIGHT - 6"
          text-anchor="middle"
          :class="$style.axisLabel"
        >{{ tick.label }}</text>
      </svg>

      <div :class="$style.legend">
        <span v-for="line in lines" :key="line.key" :class="$style.legendItem">
          <i :class="$style.swatch" :style="{ background: line.color }" />{{ line.label }}
        </span>
        <span v-if="collectedPath" :class="$style.legendItem">
          <i :class="[$style.swatch, $style.swatchDashed]" />Collected
        </span>
      </div>
    </template>
  </div>
</template>

<style module>
.chartArea {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.chart {
  display: block;
  max-width: 100%;
  height: 240px;
  overflow: visible;
}
.grid {
  stroke: var(--border-color-light);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}
.axisLabel {
  font-size: 10px;
  fill: var(--faint-color);
}
.area {
  opacity: 0.12;
}
.line {
  fill: none;
  stroke-width: 2;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}
.collected {
  fill: none;
  stroke: var(--pfs-orange);
  stroke-width: 2;
  stroke-dasharray: 4 4;
  vector-effect: non-scaling-stroke;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
}
.legendItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.swatch {
  width: 10px;
  height: 3px;
  border-radius: 2px;
  display: inline-block;
}
.swatchDashed {
  background: repeating-linear-gradient(
    to right, var(--pfs-orange) 0 3px, transparent 3px 6px
  );
}
.empty {
  margin: 0;
  padding: 40px 0;
  text-align: center;
  color: var(--faint-color);
  font-size: var(--font-size-lg);
}
</style>
